import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
    MdVerified,
    MdPerson,
    MdEmail,
    MdPhone,
    MdArrowBack,
    MdSupportAgent,
    MdInfoOutline,
} from 'react-icons/md';
import { BASE_API_URL } from '../helpers/Config';
import axios from 'axios';
import toast from 'react-hot-toast';

const getStyles = (isDark) => ({
    page: {
        maxWidth: '480px',
        margin: '0 auto',
        minHeight: '100%',
        backgroundColor: isDark ? '#111827' : '#f8fafc',
        color: isDark ? '#f9fafb' : '#111827',
    },
    hero: {
        background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 55%, #5b21b6 100%)',
        borderRadius: '16px',
        boxShadow: isDark
            ? '0 12px 32px rgba(99, 102, 241, 0.25)'
            : '0 12px 28px rgba(99, 102, 241, 0.18)',
    },
    avatarRing: {
        padding: '3px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.55) 100%)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    },
    userIdPill: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        backgroundColor: 'rgba(255,255,255,0.18)',
        color: '#f8fafc',
        border: '1px solid rgba(255,255,255,0.25)',
    },
    notice: {
        borderRadius: '12px',
        padding: '14px 16px',
        backgroundColor: isDark ? 'rgba(99, 102, 241, 0.12)' : '#eef2ff',
        border: `1px solid ${isDark ? 'rgba(129, 140, 248, 0.35)' : '#c7d2fe'}`,
    },
    noticeIcon: {
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backgroundColor: isDark ? 'rgba(99, 102, 241, 0.25)' : '#e0e7ff',
        color: isDark ? '#a5b4fc' : '#4f46e5',
    },
    card: {
        backgroundColor: isDark ? '#020617' : '#ffffff',
        borderRadius: '14px',
        border: `1px solid ${isDark ? '#1f2937' : '#e5e7eb'}`,
        boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.35)' : '0 4px 16px rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
    },
    sectionTitle: {
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: isDark ? '#9ca3af' : '#6b7280',
        marginBottom: 0,
    },
    fieldBorder: isDark ? '#1f2937' : '#f1f5f9',
    iconWrap: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backgroundColor: isDark ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff',
        color: isDark ? '#a5b4fc' : '#4f46e5',
    },
    fieldLabel: {
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: isDark ? '#9ca3af' : '#6b7280',
        marginBottom: '2px',
    },
    fieldValue: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: isDark ? '#f9fafb' : '#111827',
        wordBreak: 'break-word',
    },
    viewOnlyBadge: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: '6px',
        backgroundColor: isDark ? 'rgba(148, 163, 184, 0.15)' : '#f1f5f9',
        color: isDark ? '#94a3b8' : '#64748b',
    },
    backBtn: {
        borderRadius: '12px',
        padding: '10px 16px',
        fontWeight: 600,
        fontSize: '0.875rem',
        border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
        backgroundColor: isDark ? '#020617' : '#ffffff',
        color: isDark ? '#e5e7eb' : '#374151',
    },
    supportBtn: {
        borderRadius: '10px',
        fontWeight: 600,
        fontSize: '0.8rem',
        padding: '8px 14px',
        background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
        border: 'none',
        color: '#ffffff',
        boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
    },
    textMuted: {
        color: isDark ? '#9ca3af' : '#6b7280',
        fontSize: '0.8rem',
        lineHeight: 1.5,
    },
    skeleton: {
        backgroundColor: isDark ? '#1f2937' : '#e5e7eb',
        borderRadius: '6px',
    },
});

const ProfileField = ({ icon: Icon, label, value, styles, isLast }) => (
    <div
        className="px-3 py-3"
        style={{
            borderBottom: isLast ? 'none' : `1px solid ${styles.fieldBorder}`,
        }}
    >
        <div className="d-flex align-items-center">
            <div style={styles.iconWrap}>
                <Icon size={20} />
            </div>
            <div className="flex-grow-1 ms-3 min-w-0">
                <div style={styles.fieldLabel}>{label}</div>
                <div style={styles.fieldValue}>{value || '—'}</div>
            </div>
        </div>
    </div>
);

const EditProfileScreen = ({ theme = 'dark' }) => {
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        user_id: '',
        mobile: '',
        country_code: '91',
    });
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();
    const isDark = theme === 'dark';
    const styles = useMemo(() => getStyles(isDark), [isDark]);

    const goToSupport = () => navigate('../support');

    const FetchProfile = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) {
                setProfile({
                    name: data.data.name || '',
                    email: data.data.email || '',
                    mobile: data.data.mobile || '',
                    country_code: data.data.country_code || '91',
                    user_id: data.data.user_id || '',
                });
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        FetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fullPhone = profile.mobile
        ? `+${profile.country_code} ${profile.mobile}`
        : '—';

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.08, delayChildren: 0.05 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 14 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="h-100 overflow-auto px-3 py-3"
            style={styles.page}
        >
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                style={{ maxWidth: '480px', margin: '0 auto' }}
            >
                {/* Top bar */}
                <motion.div variants={itemVariants} className="d-flex align-items-center mb-3">
                    <Link
                        to="../profile"
                        className="btn btn-link p-0 me-2 text-decoration-none d-flex align-items-center"
                        style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
                    >
                        <MdArrowBack size={22} />
                    </Link>
                    <div className="flex-grow-1">
                        <h5
                            className="mb-0 fw-bold"
                            style={{ fontSize: '1.1rem', color: isDark ? '#f9fafb' : '#111827' }}
                        >
                            My Profile
                        </h5>
                        <p className="mb-0" style={styles.textMuted}>
                            Account overview
                        </p>
                    </div>
                    <span style={styles.viewOnlyBadge}>View only</span>
                </motion.div>

                {/* Hero */}
                <motion.div variants={itemVariants} className="mb-3 p-4 text-center" style={styles.hero}>
                    <div className="d-inline-block mb-3" style={styles.avatarRing}>
                        <img
                            src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=120&q=80"
                            alt="Profile"
                            className="rounded-circle object-fit-cover"
                            style={{ width: '88px', height: '88px', display: 'block' }}
                        />
                    </div>

                    {loading ? (
                        <>
                            <div
                                className="mx-auto mb-2"
                                style={{ ...styles.skeleton, width: '160px', height: '22px' }}
                            />
                            <div
                                className="mx-auto"
                                style={{ ...styles.skeleton, width: '120px', height: '14px' }}
                            />
                        </>
                    ) : (
                        <>
                            <div className="d-flex align-items-center justify-content-center mb-2">
                                <h4 className="h5 fw-bold mb-0 me-1 text-white">{profile.name || '—'}</h4>
                                <span className="text-white" style={{ opacity: 0.95 }} title="Verified">
                                    <MdVerified size={20} />
                                </span>
                            </div>
                            {profile.user_id && (
                                <span style={styles.userIdPill}>{profile.user_id}</span>
                            )}
                        </>
                    )}
                </motion.div>

                {/* Notice */}
                <motion.div variants={itemVariants} className="mb-3" style={styles.notice}>
                    <div className="d-flex align-items-start gap-3">
                        <div style={styles.noticeIcon}>
                            <MdInfoOutline size={20} />
                        </div>
                        <div className="flex-grow-1 min-w-0">
                            <p
                                className="mb-2 fw-semibold"
                                style={{
                                    fontSize: '0.85rem',
                                    color: isDark ? '#e5e7eb' : '#1e293b',
                                }}
                            >
                                Profile updates via support
                            </p>
                            <p className="mb-3" style={{ ...styles.textMuted, marginBottom: '12px' }}>
                                Your details are locked for security. Contact our support team to change
                                name, email, or phone number.
                            </p>
                            <motion.button
                                type="button"
                                className="btn btn-sm d-inline-flex align-items-center gap-1"
                                style={styles.supportBtn}
                                onClick={goToSupport}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <MdSupportAgent size={18} />
                                Contact Support
                            </motion.button>
                        </div>
                    </div>
                </motion.div>

                {/* Account details */}
                <motion.div variants={itemVariants} style={styles.card} className="mb-3">
                    <div
                        className="px-3 py-3"
                        style={{
                            borderBottom: `1px solid ${styles.fieldBorder}`,
                            backgroundColor: isDark ? 'rgba(15, 23, 42, 0.5)' : '#f8fafc',
                        }}
                    >
                        <p style={styles.sectionTitle}>Account details</p>
                    </div>

                    {loading ? (
                        <div className="p-3">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="mb-3"
                                    style={{ ...styles.skeleton, height: '48px', width: '100%' }}
                                />
                            ))}
                        </div>
                    ) : (
                        <>
                            <ProfileField
                                icon={MdPerson}
                                label="Full name"
                                value={profile.name}
                                styles={styles}
                            />
                            <ProfileField
                                icon={MdEmail}
                                label="Email address"
                                value={profile.email || 'Not provided'}
                                styles={styles}
                            />
                            <ProfileField
                                icon={MdPhone}
                                label="Phone number"
                                value={fullPhone}
                                styles={styles}
                                isLast
                            />
                        </>
                    )}
                </motion.div>

                {/* Back */}
                <motion.div variants={itemVariants}>
                    <Link
                        to="../profile"
                        className="btn w-100 d-flex align-items-center justify-content-center gap-2 text-decoration-none"
                        style={styles.backBtn}
                    >
                        <MdArrowBack size={18} />
                        Back to Profile
                    </Link>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default EditProfileScreen;
